import React from "react";
import { ShieldCheck, BookOpen } from "lucide-react";

/**
 * Circular progress ring used for diet nutrients (fitness-tracker style).
 * @param {number} value  current amount
 * @param {number} budget target amount (goes to 100% when reached)
 * @param {string} color  ring stroke color
 * @param {string} label  short label (e.g. "Sodium")
 * @param {string} unit   unit (e.g. "mg")
 * @param {number} size   pixel diameter (default 140)
 * @param {"prescribed"|"guideline"} source  where the budget came from
 * @param {number} guidelineValue when source=prescribed, the guideline value for comparison
 */
export default function BudgetRing({
  value, budget, color, label, unit, size = 140, testId,
  source = "guideline", guidelineValue,
}) {
  const pct = Math.min((value / budget) * 100, 999);
  const capped = Math.min(pct, 100);
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - capped / 100);
  const overBudget = pct > 100;
  const nearBudget = pct >= 85 && pct <= 100;

  const ringColor = overBudget ? "#F87171" : nearBudget ? "#FBBF24" : color;
  const isPrescribed = source === "prescribed";

  return (
    <div className="flex flex-col items-center" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="#1C201E"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={ringColor}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 900ms ease, stroke 250ms ease",
              filter: `drop-shadow(0 0 10px ${ringColor}55)`,
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="ros-metric text-2xl">{Math.round(value)}</div>
          <div className="text-[10px] text-[#A3ACA7] mt-0.5">/ {budget} {unit}</div>
        </div>
      </div>
      <div className="ros-label mt-3">{label}</div>
      <div className="flex items-center gap-1.5 mt-1">
        <div
          className="text-xs"
          style={{ color: overBudget ? "#F87171" : nearBudget ? "#FBBF24" : "#A3ACA7" }}
        >
          {Math.round(pct)}%
        </div>
        <span
          className={`inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${
            isPrescribed
              ? "border-[#4ADE80]/40 text-[#4ADE80] bg-[#4ADE80]/10"
              : "border-[#272E2B] text-[#A3ACA7] bg-transparent"
          }`}
          title={isPrescribed && guidelineValue ? `Prescribed override (guideline: ${guidelineValue})` : "General KDIGO guideline"}
          data-testid={`${testId}-source-chip`}
        >
          {isPrescribed ? <ShieldCheck size={9} /> : <BookOpen size={9} />}
          {isPrescribed ? "Prescribed" : "Guideline"}
        </span>
      </div>
    </div>
  );
}
